import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Loader2, Eye, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ESTILO_OPCIONES } from '@/invitation/payload/styles';
import type { EstiloId } from '@/invitation/payload/types';
import { useCreateInvitation } from '@/hooks/useInvitations';
import { buildStylePreviewUrl } from '@/lib/invitation/stylePreviewUrl';
import { cn } from '@/lib/utils';

/** Descriptor breve por estilo (solo para el selector; el estilo real lo define el DS). */
const ESTILO_DESC: Record<EstiloId, string> = {
  elegante: 'Serif clásica, dorados y líneas finas.',
  rustico: 'Tonos tierra, texturas y botánico.',
  costero: 'Azules serenos, aire mediterráneo.',
  urbano: 'Minimalista, moderno y sobrio.',
  romantico: 'Suave, floral y delicado.',
  epoca: 'Vintage, con aire nostálgico.',
  moderno: 'Limpio, geométrico y actual.',
};

interface StyleStepProps {
  selectedStyle: EstiloId;
  onSelectStyle: (estilo: EstiloId) => void;
  brideName: string;
  groomName: string;
  weddingDate: string;
}

/**
 * 011 — Paso final del wizard: elegir 1 de las 7 variantes del DS v2 y crear la invitación.
 * Cada variante tiene un botón "Vista previa" que abre la invitación de prueba real (ruta demo
 * `/invitations/demo/:estilo`, todas las secciones con datos de ejemplo + nombres reales) en
 * un modal. Sin colores personalizados.
 */
const StyleStep = ({
  selectedStyle,
  onSelectStyle,
  brideName,
  groomName,
  weddingDate,
}: StyleStepProps) => {
  const navigate = useNavigate();
  const createInvitation = useCreateInvitation();
  const [previewEstilo, setPreviewEstilo] = useState<EstiloId | null>(null);

  const canCreate =
    Boolean(brideName.trim()) &&
    Boolean(groomName.trim()) &&
    Boolean(weddingDate) &&
    Boolean(selectedStyle) &&
    !createInvitation.isPending;

  const handleCreate = async () => {
    if (!canCreate) return;
    try {
      const result = await createInvitation.mutateAsync({
        bride_name: brideName,
        groom_name: groomName,
        wedding_date: weddingDate,
        template_id: selectedStyle,
        is_published: false,
      });
      navigate(`/invitations/${result.id}/edit`);
    } catch {
      // El error ya lo notifica la mutación (toast).
    }
  };

  const previewMeta = previewEstilo
    ? ESTILO_OPCIONES.find((e) => e.id === previewEstilo)
    : null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-gold" />
          <span className="text-sm tracking-widest uppercase text-muted-foreground">Elige tu estilo</span>
          <Sparkles className="w-5 h-5 text-gold" />
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-3">
          ¿Cómo quieres que luzca?
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Cada estilo define paleta, tipografía y ornamentos. Toca <strong>Vista previa</strong> para
          ver una invitación de ejemplo con ese estilo. Podrás cambiarlo y afinar todo después.
        </p>
      </motion.div>

      {/* Grilla de variantes (mobile-first) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {ESTILO_OPCIONES.map((estilo) => {
          const selected = selectedStyle === estilo.id;
          return (
            <div
              key={estilo.id}
              className={cn(
                'rounded-2xl border-2 p-5 flex flex-col gap-3 transition-all bg-card',
                selected
                  ? 'border-primary ring-2 ring-primary/30 shadow-lg'
                  : 'border-border hover:border-primary/50 hover:shadow-md',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-xl text-foreground">{estilo.nombre}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{ESTILO_DESC[estilo.id]}</p>
                </div>
                {selected && (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>

              <div className="mt-auto flex items-center gap-2">
                <Button
                  type="button"
                  variant={selected ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1"
                  onClick={() => onSelectStyle(estilo.id)}
                >
                  {selected ? 'Elegido' : 'Elegir'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewEstilo(estilo.id)}
                >
                  <Eye className="w-4 h-4 mr-1.5" />
                  Vista previa
                </Button>
              </div>
            </div>
          );
        })}
      </motion.div>

      {/* Acción crear */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-10 flex flex-col items-center gap-3"
      >
        <Button
          size="lg"
          onClick={handleCreate}
          disabled={!canCreate}
          className="min-w-[220px] text-lg px-8 py-6 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all bg-gradient-to-r from-primary to-primary/90"
        >
          {createInvitation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Creando...
            </>
          ) : (
            <>
              <Heart className="w-5 h-5 mr-2 fill-current" />
              Crear mi invitación
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground text-center max-w-sm">
          Podrás cambiar el estilo y afinar todos los detalles después, en el editor.
        </p>
      </motion.div>

      {/* Modal de vista previa: invitación de prueba real (todas las secciones, datos de ejemplo). */}
      <Dialog open={previewEstilo !== null} onOpenChange={(open) => !open && setPreviewEstilo(null)}>
        <DialogContent className="max-w-[440px] p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="font-serif">Vista previa · {previewMeta?.nombre}</DialogTitle>
            <DialogDescription>
              Invitación de ejemplo con este estilo. El contenido es ficticio; solo los nombres son
              los tuyos.
            </DialogDescription>
          </DialogHeader>

          {previewEstilo && (
            <iframe
              key={previewEstilo}
              src={buildStylePreviewUrl(previewEstilo, brideName, groomName)}
              title={`Vista previa ${previewMeta?.nombre}`}
              className="w-full border-0 bg-background"
              style={{ height: '70vh' }}
            />
          )}

          <DialogFooter className="px-5 py-4 border-t">
            <Button
              type="button"
              onClick={() => {
                if (previewEstilo) onSelectStyle(previewEstilo);
                setPreviewEstilo(null);
              }}
              className="w-full"
            >
              <Check className="w-4 h-4 mr-1.5" />
              Elegir este estilo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StyleStep;
